import type { SeoScoreContext } from '../context';
import { countExactPhrase } from '../keyword-match';
import { clip, fail, pass, rangeStatus, warn, type SeoRule } from '../rule';
import { T } from '../thresholds';
import { faqStartIndex, firstHeadingIndex, introParagraphs } from '../sections';
import { contentWords, normalizeFr, splitSentences, stemAll, tokenizeWords } from '../tokenize-fr';
import { SeoRuleKey, SeoScoreGroup } from '../types';
import {
  FR_AI_PHRASES,
  FR_ANGLICISMS,
  FR_CTA_INFINITIVE_MARKERS,
  FR_CTA_INVITATION_CUES,
  FR_CTA_MARKERS,
  FR_ENUMERATIVE_ADVERBS,
  FR_ENUMERATIVE_FOLLOWERS,
  FR_ENUMERATIVE_ORDINALS,
  FR_META_INTRO_MARKERS,
  FR_REASONING_LEAK_MARKERS,
  FR_SOURCE_LEAK_MARKERS,
} from '../wordlists-fr';

const G = SeoScoreGroup.STYLE;

/** Texte normalisé « mots seulement » (apostrophes → espace) pour les recherches de phrases. */
function flatNormalized(text: string): string {
  return normalizeFr(text)
    .replace(/'/g, ' ')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const CTA_IMPERATIVES = FR_CTA_MARKERS.map((m): string => flatNormalized(m));
const CTA_INFINITIVES = FR_CTA_INFINITIVE_MARKERS.map((m): string => flatNormalized(m));
const CTA_CUES = FR_CTA_INVITATION_CUES.map((m): string => flatNormalized(m));

/** Positions (début de mot) où `phrase` apparaît en mots entiers dans `padded` (texte entouré d'espaces). */
function phraseStarts(padded: string, phrase: string): number[] {
  const out: number[] = [];
  const needle = ` ${phrase} `;
  for (let i = padded.indexOf(needle); i !== -1; i = padded.indexOf(needle, i + 1)) out.push(i + 1);
  return out;
}

/**
 * Phrase qui invite le lecteur à agir : un impératif qui n'est pas précédé de « vous »
 * (sinon c'est l'indicatif, « vous prenez rendez-vous pour votre coupe »), ou un infinitif
 * introduit par une invitation (« vous pouvez réserver », « pour réserver »).
 */
function isCtaSentence(flat: string): boolean {
  const padded = ` ${flat} `;
  const imperative = CTA_IMPERATIVES.some((m): boolean =>
    phraseStarts(padded, m).some((i): boolean => !padded.slice(0, i).endsWith(' vous ')),
  );
  if (imperative) return true;
  return CTA_INFINITIVES.some((m): boolean =>
    phraseStarts(padded, m).some((i): boolean => {
      const before = padded.slice(0, i);
      return before.endsWith(' pour ') || CTA_CUES.some((c): boolean => before.includes(` ${c} `));
    }),
  );
}

function countPhrases(flat: string, phrases: readonly string[]): { total: number; found: string[] } {
  const found: string[] = [];
  let total = 0;
  const padded = ` ${flat} `;
  for (const phrase of phrases) {
    const p = flatNormalized(phrase);
    if (p.length === 0) continue;
    let idx = 0;
    let count = 0;
    const needle = ` ${p} `;
    while ((idx = padded.indexOf(needle, idx)) !== -1) {
      count++;
      idx += needle.length - 1;
    }
    if (count > 0) {
      total += count;
      found.push(count > 1 ? `${phrase} (${count}×)` : phrase);
    }
  }
  return { total, found };
}

const PHONE_RE = /(?:\+33\s?|0)[1-9](?:[\s.-]?\d{2}){4}\b/g;
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi;

/**
 * Transition énumérative en tête de phrase (« Le premier… », « Ensuite, … »), sinon `undefined`.
 * Un ordinal à article n'est retenu que suivi d'une ponctuation, d'un verbe d'état ou d'un nom
 * d'inventaire : « Le premier rendez-vous dure… » n'est pas une transition.
 */
export function enumerativeOpener(sentence: string): string | undefined {
  // Garde la virgule et les deux-points : « La première : … » et « Ensuite, … » en dépendent.
  const text = normalizeFr(sentence)
    .replace(/['’]/g, ' ')
    .replace(/[^a-z0-9,: ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const padded = `${text} `;
  const adverb = FR_ENUMERATIVE_ADVERBS.find((o): boolean => padded.startsWith(`${o} `) || padded.startsWith(`${o},`));
  if (adverb) return adverb;
  for (const ordinal of FR_ENUMERATIVE_ORDINALS) {
    if (!padded.startsWith(`${ordinal} `) && !padded.startsWith(`${ordinal},`) && !padded.startsWith(`${ordinal}:`))
      continue;
    const rest = padded.slice(ordinal.length).trim();
    if (rest.startsWith(',') || rest.startsWith(':')) return ordinal;
    const next = rest.split(' ')[0] ?? '';
    if (FR_ENUMERATIVE_FOLLOWERS.has(next)) return ordinal;
  }
  return undefined;
}

/**
 * Chaînes énumératives : par section (tout intertitre ouvre une section), les phrases de
 * paragraphe qui commencent par un ouvreur ordinal ; deux ou plus dans la même section = une
 * chaîne. Les listes sont exclues (une liste est le bon endroit pour énumérer), la FAQ aussi.
 */
export function enumerativeChains(ctx: SeoScoreContext): string[][] {
  const faqStart = faqStartIndex(ctx);
  const chains: string[][] = [];
  let current: string[] = [];
  const flush = (): void => {
    if (current.length >= 2) chains.push(current);
    current = [];
  };
  for (const b of ctx.textBlocks) {
    if (b.index >= faqStart) break;
    if (b.type === 'heading') {
      flush();
      continue;
    }
    if (b.type !== 'paragraph') continue;
    for (const s of splitSentences(b.text)) {
      if (enumerativeOpener(s)) current.push(clip(s, 60));
    }
  }
  flush();
  return chains;
}

export function lexicalDiversity(ctx: SeoScoreContext): number | null {
  const stems = stemAll(contentWords(ctx.bodyWords));
  if (stems.length < T.LEXICAL_WINDOW) return stems.length < 50 ? null : new Set(stems).size / stems.length;
  const ratios: number[] = [];
  for (let i = 0; i + T.LEXICAL_WINDOW <= stems.length; i += T.LEXICAL_WINDOW) {
    const window = stems.slice(i, i + T.LEXICAL_WINDOW);
    ratios.push(new Set(window).size / window.length);
  }
  return ratios.reduce((a, b): number => a + b, 0) / ratios.length;
}

export function repeatedNgrams(ctx: SeoScoreContext): string[] {
  const tokens = ctx.bodyWords;
  const n = T.NGRAM_N;
  if (tokens.length < n * 3) return [];
  const kw = ctx.matcher?.normalized.replace(/\s+/g, ' ') ?? '';
  const kwStems = new Set(ctx.matcher?.stems ?? []);
  const counts = new Map<string, number>();
  for (let i = 0; i + n <= tokens.length; i++) {
    const gram = tokens.slice(i, i + n).join(' ');
    counts.set(gram, (counts.get(gram) ?? 0) + 1);
  }
  const out: string[] = [];
  for (const [gram, c] of counts) {
    if (c < T.NGRAM_REPEAT_MIN) continue;
    if (kw.length > 0 && (gram.includes(kw) || kw.includes(gram))) continue;
    // Ignore les n-grammes faits uniquement de mots vides.
    const content = contentWords(gram.split(' '));
    if (content.length === 0) continue;
    // Forme naturelle de la requête (« non honorés en institut ») : sa répétition relève de
    // KW_DENSITY, pas d'ici (2026.09.5 ; sinon les deux règles se contredisaient).
    if (kwStems.size > 0 && stemAll(content).every((st): boolean => kwStems.has(st))) continue;
    out.push(`${gram} (${c}×)`);
  }
  return out;
}

export const STYLE_RULES: SeoRule[] = [
  {
    key: SeoRuleKey.EM_DASH,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const count = (ctx.fullText.match(/—/g) ?? []).length;
      if (count === 0) return pass(0, '0');
      const locations = ctx.textBlocks
        .filter((b): boolean => b.text.includes('—'))
        .slice(0, 5)
        .map((b): string => clip(b.text));
      return fail(count, '0', locations);
    },
  },
  {
    key: SeoRuleKey.AI_PHRASES,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const { total, found } = countPhrases(flatNormalized(ctx.fullText), FR_AI_PHRASES);
      if (total === 0) return pass(0, '0');
      if (total < T.AI_PHRASES.FAIL) return warn(total, '0', found);
      return fail(total, '0', found);
    },
  },
  {
    key: SeoRuleKey.ANGLICISMS,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const { total, found } = countPhrases(flatNormalized(ctx.fullText), FR_ANGLICISMS);
      if (total === 0) return pass(0, '0');
      if (total < T.ANGLICISMS.FAIL) return warn(total, '0', found);
      return fail(total, '0', found);
    },
  },
  {
    key: SeoRuleKey.EXCLAMATIONS,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const count = (ctx.bodyText.match(/!/g) ?? []).length;
      if (count <= T.EXCLAMATIONS.PASS) return pass(count, `≤ ${T.EXCLAMATIONS.PASS}`);
      if (count <= T.EXCLAMATIONS.WARN) return warn(count, `≤ ${T.EXCLAMATIONS.PASS}`);
      return fail(count, `≤ ${T.EXCLAMATIONS.PASS}`);
    },
  },
  {
    key: SeoRuleKey.RHETORICAL_QUESTIONS,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const faqStart = faqStartIndex(ctx);
      const questions = ctx.bodyBlocks
        .filter((b): boolean => b.index < faqStart)
        .flatMap((b): string[] => splitSentences(b.text))
        .filter((s): boolean => s.trim().endsWith('?'));
      const count = questions.length;
      const locations = questions.slice(0, 5).map((s): string => clip(s));
      if (count <= T.RHETORICAL_QUESTIONS.PASS) return pass(count, `≤ ${T.RHETORICAL_QUESTIONS.PASS}`);
      if (count <= T.RHETORICAL_QUESTIONS.WARN) return warn(count, `≤ ${T.RHETORICAL_QUESTIONS.PASS}`, locations);
      return fail(count, `≤ ${T.RHETORICAL_QUESTIONS.PASS}`, locations);
    },
  },
  {
    key: SeoRuleKey.NGRAM_REPETITION,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.bodyWords.length < 200) return null;
      const grams = repeatedNgrams(ctx);
      if (grams.length === 0) return pass(0, '0');
      if (grams.length === 1) return warn(1, '0', grams);
      return fail(grams.length, '0', grams.slice(0, 8));
    },
  },
  {
    key: SeoRuleKey.LEXICAL_DIVERSITY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const ttr = lexicalDiversity(ctx);
      if (ttr === null) return null;
      const value = Math.round(ttr * 100) / 100;
      if (ttr >= T.LEXICAL_DIVERSITY.PASS) return pass(value, `≥ ${T.LEXICAL_DIVERSITY.PASS}`);
      if (ttr >= T.LEXICAL_DIVERSITY.WARN) return warn(value, `≥ ${T.LEXICAL_DIVERSITY.PASS}`);
      return fail(value, `≥ ${T.LEXICAL_DIVERSITY.PASS}`);
    },
  },
  {
    key: SeoRuleKey.TRIPLET_OVERUSE,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.wordCount < 200) return null;
      const triplets = ctx.sentences.filter((s): boolean => /\b[\wÀ-ÿ'’-]+,\s+[\wÀ-ÿ'’-]+\s+et\s+[\wÀ-ÿ'’-]+/.test(s));
      const per = triplets.length / (ctx.wordCount / 100);
      const value = `${triplets.length} (${Math.round(per * 10) / 10} / 100 mots)`;
      const threshold = `≤ 1 pour ${T.TRIPLET_PER_WORDS.PASS} mots`;
      if (per <= 100 / T.TRIPLET_PER_WORDS.PASS) return pass(value, threshold);
      if (per <= 100 / T.TRIPLET_PER_WORDS.FAIL)
        return warn(
          value,
          threshold,
          triplets.slice(0, 5).map((s): string => clip(s)),
        );
      return fail(
        value,
        threshold,
        triplets.slice(0, 5).map((s): string => clip(s)),
      );
    },
  },
  {
    key: SeoRuleKey.FORBIDDEN_TERMS,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const terms = [...(ctx.options.forbidden_terms ?? []), ...(ctx.brief?.forbidden_terms ?? [])];
      if (terms.length === 0) return null;
      const found: string[] = [];
      for (const term of terms) {
        const c = countExactPhrase(ctx.fullTextNormalized, normalizeFr(term).trim());
        if (c > 0) found.push(`${term} (${c}×)`);
      }
      if (found.length === 0) return pass(0, '0');
      return fail(found.length, '0', found);
    },
  },
  {
    key: SeoRuleKey.CONTACT_IN_BODY,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const phones = ctx.bodyText.match(PHONE_RE) ?? [];
      const emails = ctx.bodyText.match(EMAIL_RE) ?? [];
      const total = phones.length + emails.length;
      if (total === 0) return pass(0, '0');
      if (total === 1) return warn(1, '0', [...phones, ...emails]);
      return fail(total, '0', [...phones, ...emails]);
    },
  },
  {
    key: SeoRuleKey.CTA_COUNT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.sentences.length < 5) return null;
      const cta = ctx.sentences.filter((s): boolean => isCtaSentence(flatNormalized(s)));
      const count = cta.length;
      const locations = cta.slice(0, 5).map((s): string => clip(s));
      return rangeStatus(count, T.CTA_COUNT.PASS, [0, T.CTA_COUNT.WARN_MAX], (v): number => v, locations);
    },
  },
  {
    key: SeoRuleKey.REASONING_LEAK,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const { total, found } = countPhrases(flatNormalized(ctx.fullText), FR_REASONING_LEAK_MARKERS);
      // L'autre signature d'un raisonnement déversé : une longue liste (plan, contraintes) AVANT le premier intertitre.
      const firstHeading = firstHeadingIndex(ctx);
      const preamble = firstHeading === -1 ? ctx.textBlocks : ctx.textBlocks.slice(0, firstHeading);
      const preambleItems = preamble.filter((b): boolean => b.type === 'list_item').length;
      const planBeforeHeading = preambleItems >= T.REASONING_PREAMBLE_ITEMS;
      const hits = total + (planBeforeHeading ? 1 : 0);
      if (hits === 0) return pass(0, '0');
      const locations = [
        ...found,
        ...(planBeforeHeading ? [`${preambleItems} éléments de liste avant le premier intertitre`] : []),
      ];
      return fail(hits, '0', locations);
    },
  },
  {
    key: SeoRuleKey.SOURCE_LEAK,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const { total, found } = countPhrases(flatNormalized(ctx.fullText), FR_SOURCE_LEAK_MARKERS);
      if (total === 0) return pass(0, '0');
      if (total < T.SOURCE_LEAK.FAIL) return warn(total, '0', found);
      return fail(total, '0', found);
    },
  },
  {
    key: SeoRuleKey.ENUMERATIVE_SCAFFOLDING,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.sentences.length < 5) return null;
      const chains = enumerativeChains(ctx);
      const locations = chains.slice(0, 3).map((c): string => c.join(' / '));
      if (chains.length <= T.ENUMERATIVE_CHAINS.PASS) return pass(chains.length, '0 chaîne');
      if (chains.length <= T.ENUMERATIVE_CHAINS.WARN) return warn(chains.length, '0 chaîne', locations);
      return fail(chains.length, '0 chaîne', locations);
    },
  },
  {
    key: SeoRuleKey.META_INTRO,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const intro = introParagraphs(ctx);
      if (intro.length === 0) return null;
      const { total, found } = countPhrases(
        flatNormalized(intro.map((b): string => b.text).join(' ')),
        FR_META_INTRO_MARKERS,
      );
      if (total === 0) return pass(0, '0');
      if (total < T.META_INTRO.FAIL) return warn(total, '0', found);
      return fail(total, '0', found);
    },
  },
];

/** Exposé pour les tests. */
export const styleFlatNormalized = flatNormalized;
export const styleTokenize = tokenizeWords;
